import {
  ExternalSceneProvider,
  ExternalSceneRowViewModel,
  ExternalSceneStatusKey
} from './external-scenes.models';
import { formatCoordinates } from './external-scenes-map.util';

/** Look of the per-row action button, derived once from the row view model. */
export interface ExternalSceneActionState {
  /** i18n key of the button label. */
  labelKey: string;
  disabled: boolean;
  loading: boolean;
}

/** Colour family of the status dot. */
export type ExternalSceneStatusTone = 'available' | 'progress' | 'done';

/** A table row: the store's view model plus the display strings only the table needs. */
export interface ExternalSceneTableRow extends ExternalSceneRowViewModel {
  coordinatesLabel: string;
  providerClass: string;
  statusTone: ExternalSceneStatusTone;
  /** True while the row has a preview URL that has not failed to load. */
  showPreview: boolean;
}

const LOAD_LABEL_KEY = 'EXTERNAL_SCENES.LOAD_BUTTON';
const LOADING_LABEL_KEY = 'EXTERNAL_SCENES.LOAD_BUTTON_LOADING';

/**
 * Load button state: "Load LRSD" only while the row can be loaded; "Sending..." during the request; afterwards
 * the row's own status ("pending" / "downloading" / "downloaded") on a disabled button.
 */
export function resolveActionState(
  row: Pick<ExternalSceneRowViewModel, 'canLoad' | 'isLoading' | 'statusKey'>
): ExternalSceneActionState {
  if (row.isLoading) {
    return { labelKey: LOADING_LABEL_KEY, disabled: true, loading: true };
  }
  if (row.canLoad) {
    return { labelKey: LOAD_LABEL_KEY, disabled: false, loading: false };
  }
  const showsStatus = row.statusKey !== 'EXTERNAL_SCENES.STATUS_NOT_LOADED';
  return { labelKey: showsStatus ? row.statusKey : LOAD_LABEL_KEY, disabled: true, loading: false };
}

export function resolveStatusTone(statusKey: ExternalSceneStatusKey): ExternalSceneStatusTone {
  switch (statusKey) {
    case 'EXTERNAL_SCENES.STATUS_DOWNLOADED':
      return 'done';
    case 'EXTERNAL_SCENES.STATUS_DOWNLOADING':
    case 'EXTERNAL_SCENES.STATUS_PENDING':
      return 'progress';
    default:
      return 'available';
  }
}

export function resolveProviderClass(provider: ExternalSceneProvider): string {
  return provider === 'Sentinel2' ? 'provider--sentinel' : 'provider--landsat';
}

export function toTableRow(
  row: ExternalSceneRowViewModel,
  failedPreviewIds: ReadonlySet<string> = new Set()
): ExternalSceneTableRow {
  return {
    ...row,
    coordinatesLabel: formatCoordinates(row.centerLatitude, row.centerLongitude),
    providerClass: resolveProviderClass(row.provider),
    statusTone: resolveStatusTone(row.statusKey),
    showPreview: row.previewUrl !== null && !failedPreviewIds.has(row.id)
  };
}
